import React, { lazy, Suspense, useEffect, useState } from 'react';

// Creates a component that uses React.lazy on the client and
// a synchronous require on the server to preserve SSR behavior
export default function isomorphicRoute(importer, options = {}) {
  const isServer = typeof window === 'undefined';

  if (isServer) {
    // On the server we can synchronously require the component using the route's componentPath
    return function ServerIsomorphicRoute(props) {
      try {
        const componentPath = props?.route?.componentPath;
        if (!componentPath) return null;
        // eslint-disable-next-line global-require, import/no-dynamic-require
        const Mod = require(`@client/${componentPath}`).default;
        if (!Mod) return null;
        return <Mod {...props} />;
      } catch (e) {
        if (process.env.NODE_ENV === 'development') {
          // eslint-disable-next-line no-console
          console.error('SSR isomorphicRoute failed to load component', e);
        }
        return null;
      }
    };
  }

  // Always define LazyComp; decide at render-time whether to use a preloaded module
  const LazyComp = lazy(importer);
  const Loading = options.loading || (() => null);

  function LazyOnClient(props) {
    const Preloaded = importer && importer.__preloaded;
    if (Preloaded) {
      const Resolved = Preloaded;
      return <Resolved {...props} />;
    }

    return (
      <Suspense fallback={null}>
        <LazyComp {...props} />
      </Suspense>
    );
  }

  // Expose a preload hook so the client can fetch the chunk before hydration
  LazyOnClient.preload = () =>
    importer().then((m) => {
      try {
        importer.__preloaded = (m && (m.default || m)) || null;
      } catch (_) {}
      return m;
    });

  return LazyOnClient;
}
